<template>
  <div class="icon_section">
    <div class="input_container">
      <div v-show="search.length >= 2" class="clear" @click="search = ''">
        <svg-icon
          ref="icon"
          :width="'16'"
          :height="'16'"
          icon="app-icons/close"
          fill="#fff"
        />
      </div>
      <input
        v-model="search"
        :spellcheck="false"
        class="search"
        placeholder="Search icons..."
      />
    </div>
    <div v-if="search" class="iconset">
      <div
        v-for="(icon, index) in icons"
        :key="index"
        v-tooltip="{
          content: iconName(icon),
          placement: 'top',
          delay: { show: 500, hide: 100 },
        }"
        class="button"
        @click="onClickIcon(icon)"
      >
        <svg-icon ref="icon" :icon="icon" :width="'52'" :height="'52'" />
      </div>
    </div>
    <div v-else class="tags">
      <div
        v-for="tag in tags"
        :key="tag.desc"
        class="button"
        @click="onClickTag($event, tag.tag)"
      >
        <svg-icon :icon="tagIcon(tag.tag)" :width="'32'" :height="'32'" />
        <span class="name">
          {{ name(tag.tag) + ' (' + tag.num + ')' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import SvgIcon from '../SvgIcon.vue';
import iconMetadata from '../../../vuex/icon-metadata.js';
import { kebabCaseToTitleCase } from '../../../utils.js';

export default {
  components: {
    SvgIcon,
  },

  props: {
    // The current asset that is being replaced (optional)
    assetReference: {
      type: Object,
      default: null,
    },
    // A callback to use when the user selects an asset
    onSelect: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      tags: iconMetadata.tags,
      search: '',
      icons: [],
    };
  },

  computed: {},

  watch: {
    /**
     * Watcher for a change in the search term. Initiates a (debounced) search
     */
    async search(newValue, oldValue) {
      // If the search contains less than 2 letters don't do it
      if (newValue.length < 2) {
        this.icons = [];
        return;
      }

      await this.performSearch(newValue);
    },
  },

  created() {
    // Debounce search so that it is triggered at most every 500 msec
    this.performSearch = _.debounce(this.performSearch, 500);
  },

  methods: {
    /**
     * Starts a search and loads the results in chunks
     * @param {string}  search The search term
     */
    async performSearch(search) {
      // First find the search results
      const icons = iconMetadata.getIconsBySearch(search.toLowerCase());
      // Create the icons as spinners until they are loaded
      this.icons = icons.map(icon => 'app-icons/spinner');
      // Break the search results into chunks
      const chunkSize = 20;
      const chunks = _.chunk(icons, chunkSize);
      // Run the multiple requests sequentially
      try {
        await chunks.reduce(async (promise, chunk, index, arr) => {
          await promise;
          const p = await iconMetadata.getIcons(chunk);
          // Break the reduce if the search term has changed
          if (this.search !== search) {
            return Promise.reject(new Error('Search changed'));
          }
          // Change each loaded icon from spinner to its real name
          chunk.forEach((icon, chunkIndex) => {
            this.icons[index * chunkSize + chunkIndex] = icon;
          });
          return p;
        }, Promise.resolve());
      } catch (error) {
        console.log('Promise rejection from changing icon search pattern.');
      }
    },

    /**
     * Event handler: User clicked on an icon from the icon list. Changes the icon.
     * @param {string}  icon    The icon
     */
    onClickIcon(icon) {
      // Execute the select callback function
      this.onSelect({ type: 'icon', file: icon });
    },

    /**
     * Event handler: User clicked on a tag button. Searches the icons using the tag.
     * @param {Object}  event
     * @param {string}  tag     The name of the clicked tag
     */
    onClickTag(event, tag) {
      this.search = tag;
    },

    /**
     * Return the url of a tag icon
     * @param {string}  tag     The name of the tag
     */
    tagIcon(tag) {
      return iconMetadata.getTagIcon(tag);
    },

    /**
     * Return the name of an icon
     * @param {string}  icon    The icon in the format artist/icon-name
     */
    iconName(icon) {
      return iconMetadata.getIconName(icon);
    },

    name(tag) {
      return kebabCaseToTitleCase(tag);
    },
  },
};
</script>

<style lang="scss"></style>

<style lang="scss" scoped>
.icon_section {
  padding: 15px;

  .img_container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 94px;
  }
  .iconset {
    overflow-y: auto;
    overflow-x: hidden;
    display: grid;
    max-height: 260px;
    min-height: 90px; //for the tooltip
    padding: 0 0 10px 0; //for last row icon scaling
    grid-template-columns: repeat(auto-fill, 16%);

    .button {
      margin: 5px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      max-height: 54px;
      .svg-icon-container {
        :deep(svg) {
          transition: transform 0.2s ease;
          path {
            fill: #bcbcbc;
          }
        }
      }

      &:hover {
        .svg-icon-container {
          // position: relative;
          :deep(svg) {
            // position: absolute;
            transform: scale(1.25);
            path {
              fill: #fff;
            }
          }
        }
      }
    }
  }
}
.input_container {
  position: relative;
  .clear {
    position: absolute;
    opacity: 0.25;
    top: 12px;
    right: 14px;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  }
  .search {
    width: 100%;
    height: 40px;
    background: #141616;
    border: none;
    border-radius: 4px;
    padding: 10px 14px 8px 14px;
    color: #fff;
    margin: 0 0 10px 0;
    &:focus {
      background: #0a0a0a;
    }
  }
}
.tags {
  max-height: 260px;
  overflow: auto;
  display: grid; /* 1 */
  grid-template-columns: repeat(auto-fill, 50%); /* 2 */
  grid-gap: 0; /* 3 */
  justify-content: space-between; /* 4 */
  overflow-y: scroll;
  padding-right: 16px;
  .button {
    display: flex;
    align-items: center;
    padding: 5px 6px 5px;
    // width: calc(100% - 5px);

    border-radius: 3px;
    cursor: pointer;
    &:hover {
      background: #363e3f;
      img {
        opacity: 1;
      }
      .name {
        color: #fff;
      }
      :deep(svg path) {
        fill: #fff;
      }
    }
    img {
      opacity: 0.5;
    }
    .name {
      padding: 6px 8px;
      font-size: 14px;
      color: #bcbcbc;
    }
  }
}

.upload-btn-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  overflow: hidden;
  display: inline-block;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
  input[type='file'] {
    font-size: 100px;
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  ::-webkit-file-upload-button {
    cursor: pointer;
  }
  .btn {
    color: #fff;
    // background-color: #db841e;
    padding: 0 9px 8px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 500;
    font-family: 'open sans';
    text-transform: initial;
    cursor: pointer;
    align-items: center;
    display: flex;

    span {
      margin-left: 5px;
    }
  }
}

.iconset::-webkit-scrollbar-track,
.tags::-webkit-scrollbar-track {
  background-color: #1e2121;
  border: none;
  border-radius: 999px;
}

.iconset::-webkit-scrollbar,
.tags::-webkit-scrollbar {
  width: 8px;
  background-color: transparent;
}

.iconset::-webkit-scrollbar-thumb,
.tags::-webkit-scrollbar-thumb {
  width: 8px;
  background-color: #db841e;
  border-radius: 999px;
}

.iconset::-webkit-scrollbar-thumb:hover,
.tags::-webkit-scrollbar-thumb:hover {
  background-color: #db841e;
}
</style>
